import { apiClient } from '../client';
import { API_ENDPOINTS, CACHE_CONFIG } from '../config';

interface AutocompleteParams {
  type: 'ai' | 'providers' | 'procedures' | 'medications';
  q: string;
}

interface SearchParams {
  type: 'ai' | 'providers' | 'procedures' | 'medications';
  q: string;
  filters?: Record<string, any>;
}

interface AISearchResponse {
  sessionId: string;
  initialResponse: string;
  question?: any;
}

interface AIFollowupResponse {
  success: boolean;
  sessionId: string;
  response?: string;
  question?: any;
  carePlan?: any;
  providers?: any[];
}

interface PopularProcedure {
  name: string;
  searchCount: number;
  averagePrice: number;
}

interface RecentSearch {
  id: string;
  type: string;
  npi?: string;
  name: string;
  specialty?: string;
  distance?: string;
  address?: string;
  procedure?: string;
  price?: string;
  image: string;
  isChecked?: boolean;
  lastSearched: string;
}

interface CarePlan {
  id: string;
  title: string;
  description: string;
  condition: string;
  duration: string;
  estimatedCost: {
    min: number;
    max: number;
  };
  providerCount: number;
  medicationCount: number;
  procedureCount: number;
  successRate: number;
  keyBenefits: string[];
  recommendationScore: number;
  tags: string[];
}

class SearchService {
  async autocomplete(params: AutocompleteParams) {
    const queryParams = new URLSearchParams(params as any);
    return apiClient.get(`${API_ENDPOINTS.search.autocomplete}?${queryParams}`, {
      cacheTTL: CACHE_CONFIG.defaultTTL
    });
  }

  async search(params: SearchParams) {
    const queryParams = new URLSearchParams();
    queryParams.append('type', params.type);
    queryParams.append('q', params.q);
    
    if (params.filters) {
      Object.entries(params.filters).forEach(([key, value]) => {
        queryParams.append(key, value);
      });
    }

    return apiClient.get(`${API_ENDPOINTS.search.main}?${queryParams}`, {
      cacheTTL: CACHE_CONFIG.defaultTTL
    });
  }

  async getPopularProcedures(): Promise<{ success: boolean; data: PopularProcedure[] }> {
    return apiClient.get(API_ENDPOINTS.search.popularProcedures, {
      cacheTTL: CACHE_CONFIG.staticDataTTL
    });
  }

  async getRecentSearches(): Promise<{ success: boolean; data: RecentSearch[] }> {
    return apiClient.get(API_ENDPOINTS.search.recent, {
      cacheTTL: CACHE_CONFIG.defaultTTL
    });
  }

  async startAISearch(query: string): Promise<AISearchResponse> {
    return apiClient.post(API_ENDPOINTS.search.ai.start, { query });
  }

  async submitAIFollowup(sessionId: string, answer: any): Promise<AIFollowupResponse> {
    return apiClient.post(API_ENDPOINTS.search.ai.followup, { sessionId, answer });
  }

  async getAISearchDetail(sessionId: string) {
    return apiClient.get(`${API_ENDPOINTS.search.ai.detail}?sessionId=${sessionId}`, {
      cacheTTL: CACHE_CONFIG.defaultTTL
    });
  }

  async getAICarePlans(query: string): Promise<{ success: boolean; query: string; data: CarePlan[] }> {
    return apiClient.get(`${API_ENDPOINTS.search.ai.carePlans}?q=${encodeURIComponent(query)}`, {
      cacheTTL: CACHE_CONFIG.defaultTTL
    });
  }
}

export const searchService = new SearchService();